// Company Drive lists (NHP, clinic Misc HR, Documents, Vendors).
//
// These folders barely change. Walking Google Drive on every open is why
// Company → HR feels slow. The first successful read is saved; later opens
// paint that copy. A later check only re-lists folders whose stamp changed.

import { authedGet } from './apiClient';
import { session } from './session';
import { persistResource, peekResource, refetchResource } from './store';

export const COMPANY_HR_KEY = 'company-drive:hr:v4';

export function companyDriveKey(
  pane: 'documents' | 'vendors' | 'management' | 'hr',
  location?: string,
): string {
  if (pane === 'hr') return COMPANY_HR_KEY;
  return `company-drive:${pane}:${location ?? ''}:v4`;
}

export function isCompanyDriveKey(key: string): boolean {
  return key === COMPANY_HR_KEY || key.startsWith('company-drive:');
}

function withOwner<T extends object>(data: T): T & { owner?: string } {
  return { ...data, owner: session.value.me?.email ?? '' };
}

type DriveSection = {
  name: string;
  files?: unknown[];
  folderModified?: string;
  unchanged?: boolean;
  folderUrl?: string;
  reason?: string;
};

type DriveHrTopic = {
  name: string;
  sections?: DriveSection[];
};

type DriveHrData = {
  topics?: DriveHrTopic[];
  [key: string]: unknown;
};

type DrivePaneData = {
  sections?: DriveSection[];
  [key: string]: unknown;
};

export function stampsFromSections(sections: DriveSection[] | undefined, prefix?: string): string {
  return (sections || [])
    .filter((s) => s.name && s.folderModified)
    .map((s) => {
      const name = prefix ? `${prefix}/${s.name}` : s.name;
      return `${encodeURIComponent(name)}::${encodeURIComponent(s.folderModified as string)}`;
    })
    .join(';');
}

export function stampsFromHr(data: DriveHrData | undefined): string {
  return (data?.topics || [])
    .map((t) => stampsFromSections(t.sections, t.name))
    .filter(Boolean)
    .join(';');
}

export function mergeDriveSections(
  previous: DriveSection[] | undefined,
  incoming: DriveSection[] | undefined,
): DriveSection[] {
  const prevByName = new Map((previous || []).map((s) => [s.name, s]));
  return (incoming || []).map((s) => {
    if (s.unchanged && prevByName.has(s.name)) {
      const kept = prevByName.get(s.name) as DriveSection;
      return {
        ...kept,
        folderModified: s.folderModified || kept.folderModified,
        folderUrl: s.folderUrl || kept.folderUrl,
        unchanged: undefined,
      };
    }
    const { unchanged: _drop, ...rest } = s;
    return rest;
  });
}

export function mergeHrData(previous: DriveHrData | undefined, incoming: DriveHrData): DriveHrData {
  const prevByName = new Map((previous?.topics || []).map((t) => [t.name, t]));
  return {
    ...incoming,
    topics: (incoming.topics || []).map((topic) => {
      const prior = prevByName.get(topic.name);
      return {
        ...topic,
        sections: mergeDriveSections(prior?.sections, topic.sections),
      };
    }),
  };
}

const inflight = new Map<string, Promise<unknown>>();

function once(key: string, work: () => Promise<unknown>): Promise<unknown> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const pending = work().finally(() => inflight.delete(key));
  inflight.set(key, pending);
  return pending;
}

export async function fetchCompanyHr(opts?: { force?: boolean }): Promise<unknown> {
  return once(`${COMPANY_HR_KEY}:${opts?.force ? 'force' : 'check'}`, async () => {
    const previous = peekResource<DriveHrData>(COMPANY_HR_KEY) ?? undefined;
    const params = new URLSearchParams({ pane: 'hr' });
    if (!opts?.force) {
      const known = stampsFromHr(previous);
      if (known) params.set('known', known);
    }
    const data = await authedGet<DriveHrData>(`/api/v1/company/drive?${params.toString()}`);
    const merged = withOwner(mergeHrData(previous, data));
    await persistResource(COMPANY_HR_KEY, merged);
    return merged;
  });
}

export async function fetchCompanyDrivePane(
  pane: 'documents' | 'vendors' | 'management',
  location?: string,
  opts?: { force?: boolean },
): Promise<unknown> {
  const key = companyDriveKey(pane, location);
  return once(`${key}:${opts?.force ? 'force' : 'check'}`, async () => {
    const previous = peekResource<DrivePaneData>(key);
    const params = new URLSearchParams({ pane });
    if (location) params.set('location', location);
    if (!opts?.force) {
      const known = stampsFromSections(previous?.sections);
      if (known) params.set('known', known);
    }
    const data = await authedGet<DrivePaneData>(`/api/v1/company/drive?${params.toString()}`);
    const merged = withOwner({
      ...data,
      sections: mergeDriveSections(previous?.sections, data.sections),
    });
    await persistResource(key, merged);
    return merged;
  });
}

export async function fetchCompanyDriveByKey(key: string): Promise<unknown> {
  if (key === COMPANY_HR_KEY) return fetchCompanyHr();
  const rest = key.slice('company-drive:'.length);
  const [pane, location] = rest.split(':');
  if (pane === 'documents' || pane === 'vendors' || pane === 'management') {
    return fetchCompanyDrivePane(pane, location || undefined, { force: true });
  }
  return fetchCompanyHr();
}

/** Fill HR from Drive once, in the background, so Company → HR is already there. */
export function warmCompanyHr(): void {
  if (peekResource(COMPANY_HR_KEY)) return;
  void fetchCompanyHr().catch(() => {
    // The HR tab still fetches and names the fault if this warm fails.
  });
}

/** Update saved Drive lists without flipping them back to "Loading…". */
export function refreshCompanyDriveLists(keys: string[]): void {
  const unique = [...new Set(keys.filter(isCompanyDriveKey))];
  for (const key of unique) {
    void refetchResource(key, () => fetchCompanyDriveByKey(key));
  }
}
